
import React from "react";
import TocTocHeader from "@/components/TocTocHeader";
import TocTocFooter from "@/components/TocTocFooter";
import { Users, BarChart3, Calendar, MessageSquare, Check, Star } from "lucide-react";
import { Button } from "@/components/ui/button";

const TocTocCRM = () => {
  const features = [
    {
      icon: Users,
      title: "Gestão de Clientes",
      description: "Organize todos os seus contatos, leads e clientes em um só lugar com informações detalhadas"
    },
    {
      icon: BarChart3,
      title: "Análise de Vendas",
      description: "Acompanhe métricas importantes, conversões e performance da sua equipe em tempo real"
    },
    {
      icon: Calendar,
      title: "Agendamento",
      description: "Gerencie visitas, reuniões e follow-ups com sistema integrado de calendário"
    },
    {
      icon: MessageSquare,
      title: "Comunicação",
      description: "Centralize WhatsApp, e-mail e outras comunicações para não perder nenhum contato"
    }
  ];

  const benefits = [
    "Aumento de até 40% na conversão de leads",
    "Redução de 60% no tempo de gestão administrativa",
    "Organização completa do pipeline de vendas",
    "Relatórios detalhados e insights valiosos",
    "Integração com principais portais imobiliários",
    "Suporte técnico especializado"
  ];

  return (
    <div className="min-h-screen flex flex-col">
      <TocTocHeader />
      <main className="flex-1">
        {/* Hero Section */}
        <section className="py-16 md:py-24 bg-gradient-to-r from-[#261E13] to-[#323031] text-white">
          <div className="container max-w-7xl mx-auto px-4">
            <div className="grid lg:grid-cols-2 gap-12 items-center">
              <div>
                <h1 className="text-4xl md:text-6xl font-bold mb-6 animate-fade-in">
                  Toc Toc <span className="text-[#FF5605]">CRM</span>
                </h1>
                <p className="text-xl text-white/90 leading-relaxed mb-8 animate-fade-in animation-delay-200">
                  O sistema completo de gestão de clientes imobiliários que revoluciona sua forma de vender. 
                  Organize leads, automatize processos e acelere suas vendas.
                </p>
                <div className="flex flex-col sm:flex-row gap-4 animate-fade-in animation-delay-400">
                  <Button className="bg-[#FF5605] hover:bg-[#E14D04] text-white px-8 py-4 rounded-xl text-lg">
                    Teste Grátis por 30 dias
                  </Button>
                  <Button variant="outline" className="border-white text-white hover:bg-white hover:text-[#323031] px-8 py-4 rounded-xl text-lg">
                    Ver Demonstração
                  </Button>
                </div>
              </div>
              <div className="relative">
                <div className="bg-white/10 backdrop-blur-sm rounded-3xl p-8 border border-white/20">
                  <div className="space-y-4">
                    <div className="flex items-center space-x-4">
                      <div className="w-12 h-12 bg-[#FF5605] rounded-xl flex items-center justify-center">
                        <Users className="h-6 w-6 text-white" />
                      </div>
                      <div>
                        <div className="font-semibold">1.247 Leads Ativos</div>
                        <div className="text-sm text-white/70">+23% este mês</div>
                      </div>
                    </div>
                    <div className="h-px bg-white/20"></div>
                    <div className="flex items-center space-x-4">
                      <div className="w-12 h-12 bg-green-500 rounded-xl flex items-center justify-center">
                        <BarChart3 className="h-6 w-6 text-white" />
                      </div>
                      <div>
                        <div className="font-semibold">R$ 2.8M em Vendas</div>
                        <div className="text-sm text-white/70">Meta: 87% atingida</div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Features Section */}
        <section className="py-24 bg-white">
          <div className="container max-w-7xl mx-auto px-4">
            <div className="text-center mb-16">
              <h2 className="text-4xl md:text-5xl font-bold text-[#323031] mb-6">
                Funcionalidades <span className="text-[#FF5605]">Completas</span>
              </h2>
              <p className="text-xl text-[#5B5A59] max-w-3xl mx-auto">
                Tudo que você precisa para gerenciar seu negócio imobiliário em uma única plataforma
              </p>
            </div>

            <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
              {features.map((feature, index) => (
                <div key={index} className="text-center p-8 rounded-3xl hover:shadow-xl transition-all duration-300 hover:scale-105 border border-gray-100">
                  <div className="w-16 h-16 bg-[#FF5605]/10 rounded-2xl flex items-center justify-center mx-auto mb-6">
                    <feature.icon className="h-8 w-8 text-[#FF5605]" />
                  </div>
                  <h3 className="text-2xl font-bold text-[#323031] mb-4">
                    {feature.title}
                  </h3>
                  <p className="text-[#5B5A59] leading-relaxed">
                    {feature.description}
                  </p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Benefits Section */}
        <section className="py-24 bg-[#F6E5C9]/20">
          <div className="container max-w-7xl mx-auto px-4">
            <div className="grid lg:grid-cols-2 gap-16 items-center">
              <div>
                <h2 className="text-4xl font-bold text-[#323031] mb-8">
                  Resultados que <span className="text-[#FF5605]">Transformam</span>
                </h2>
                <div className="space-y-4">
                  {benefits.map((benefit, index) => (
                    <div key={index} className="flex items-center space-x-4">
                      <div className="w-8 h-8 bg-[#FF5605]/10 rounded-full flex items-center justify-center flex-shrink-0">
                        <Check className="h-5 w-5 text-[#FF5605]" />
                      </div>
                      <span className="text-[#5B5A59] text-lg">{benefit}</span>
                    </div>
                  ))}
                </div>
              </div>
              <div className="relative">
                <div className="bg-white rounded-3xl p-8 shadow-2xl">
                  <div className="text-center mb-6">
                    <div className="flex justify-center space-x-1 mb-4">
                      {[...Array(5)].map((_, i) => (
                        <Star key={i} className="h-6 w-6 fill-yellow-400 text-yellow-400" />
                      ))}
                    </div>
                    <blockquote className="text-lg text-[#323031] italic mb-4">
                      "O Toc Toc CRM revolucionou nossa imobiliária. Aumentamos as vendas em 45% no primeiro trimestre!"
                    </blockquote>
                    <div className="font-semibold text-[#FF5605]">Marina Silva</div>
                    <div className="text-sm text-[#5B5A59]">Diretora Comercial - Imobiliária Sucesso</div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* CTA Section */}
        <section className="py-16 bg-gradient-to-r from-[#261E13] to-[#323031] text-white">
          <div className="container max-w-7xl mx-auto px-4 text-center">
            <h2 className="text-3xl md:text-4xl font-bold mb-6">
              Comece a vender mais <span className="text-[#FF5605]">hoje mesmo</span>
            </h2>
            <p className="text-xl text-white/90 mb-8 max-w-2xl mx-auto">
              Junte-se a mais de 500 imobiliárias que já transformaram seus resultados com o Toc Toc CRM
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Button className="bg-[#FF5605] hover:bg-[#E14D04] text-white px-12 py-4 rounded-xl text-lg hover:scale-105 transition-transform">
                Começar Teste Grátis
              </Button>
              <Button variant="outline" className="border-white text-white hover:bg-white hover:text-[#323031] px-12 py-4 rounded-xl text-lg">
                Falar com Consultor
              </Button>
            </div>
          </div>
        </section>
      </main>
      <TocTocFooter />
    </div>
  );
};

export default TocTocCRM;
